import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { sanitizeReturnUrl } from '../utils/url-sanitizer';
import { safeSessionStorageSet } from '../utils/safe-storage';

/**
 * Auth guard to protect routes that require authentication
 */
export const authGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // ensureValidSession() waits for async auth initialization (MSAL / local token restore) AND
  // silently refreshes a lapsed local access token from the httpOnly refresh cookie first.
  // Gating on the synchronous isAuthenticated() instead bounced signed-in users to /login on
  // their first navigation after the 1-hour access token expired, even though the 7-day refresh
  // cookie was still valid — the guard short-circuits before any request, so the interceptor's
  // 401 retry never got a chance to recover the session.
  if (await authService.ensureValidSession()) {
    return true;
  }

  // Sanitize return URL before storing to prevent open redirect attacks
  const safeReturnUrl = sanitizeReturnUrl(state.url);
  
  // Save return URL in session storage for redirect after login
  safeSessionStorageSet('returnUrl', safeReturnUrl);

  // Return a UrlTree to avoid navigation race conditions
  return router.createUrlTree(['/login'], {
    queryParams: { returnUrl: safeReturnUrl }
  });
};
